import {
  Grid,
  Button,
  Box,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  TextField,
  Tooltip,
  IconButton,
  InputAdornment,
  Pagination,
  Typography
  } from '@mui/material';
  import { useEffect, useRef } from 'react';
  import PropTypes from 'prop-types';
  import { useNavigate } from 'react-router-dom';
  import Iconify from '../../../reusable-components/iconify';
  import Scrollbar from '../../../reusable-components/scrollbar';
  import DateFilter from './DateFilter';
import LoadingAnimation from '../../../reusable-components/loader/LoadingAnimation';

  Search.propTypes = {
    backToPath: PropTypes.string,
    searchTypes: PropTypes.array,
    setSearchData: PropTypes.func,
    handleSearch: PropTypes.func,
    isSearchDisabled: PropTypes.bool,
    filters: PropTypes.object,
    handlePaginationChange: PropTypes.func,
    searchResults: PropTypes.object,
    searchDataMap: PropTypes.array,
    searchData: PropTypes.object,
    loading: PropTypes.bool,
    setFilters: PropTypes.func,
    filtersElements: PropTypes.object,
    searchChildren: PropTypes.object

  }
  export function Search({
    backToPath, searchTypes, setSearchData, handleSearch, loading, setFilters, filtersElements,
    isSearchDisabled, filters, handlePaginationChange, searchData, searchResults, searchDataMap, searchChildren
  }) {

    const { searchValue, selectedSearchTypes, page } = searchData;

    const navigate = useNavigate();

    const isFirstRender = useRef(true);

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      handleSearch(searchData, 1);
    }, [searchData.pageSize]);

    const handleAllFieldsClick = () => {
      const newSelectedSearchTypes = selectedSearchTypes?.length === searchTypes.length ? [] : searchTypes;
      setSearchData(data => ({...data, selectedSearchTypes: newSelectedSearchTypes}));
    }

    const handleSearchTypeClick = (_, type) => {
      const newSelectedSearchTypes = 
        !selectedSearchTypes.some(searchType => searchType.value === type.value) ? [...selectedSearchTypes, type] :
        selectedSearchTypes.filter(t => t.value !== type.value);
      setSearchData(data => ({...data, selectedSearchTypes: newSelectedSearchTypes}));
    }

  return (
    <Box sx={{ height: '82vh', display: 'flex', flexDirection: 'column', }}>
      <Box sx={{ flexShrink: 0 }}>
        <Grid container direction="row" sx={{width: '100%'}}>
          <Grid item xs={1}>
            <Button
              variant="text"
              onClick={() => navigate(backToPath)}
              startIcon={<Iconify icon="mingcute:left-fill" width={24} height={24} />}
              sx={{ color: '#454F5B', fontWeight: 'bold' }}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={11}>
            <Box sx={{ backgroundColor: '#e5e9ed', padding: '12px', borderRadius: '10px', display: 'flex' }}>
              <Grid container spacing={0.5}>
                <Grid item xs="auto">
                  <Select
                    multiple
                    variant='outlined'
                    renderValue={() => selectedSearchTypes.length === searchTypes.length ? "All Fields" : selectedSearchTypes.map(s => s.label).join(', ')}
                    value={[]}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    sx={{
                      maxWidth: '200px',
                      height: '40px',
                      backgroundColor: '#f2f5f7',
                    }}
                  >
                    <MenuItem onClick={handleAllFieldsClick}>
                      <Checkbox checked={selectedSearchTypes?.length === searchTypes.length} />
                      <ListItemText primary={"All Fields"} />
                    </MenuItem>
                    <Divider sx={{ my: '0 !important', mx: 'auto', width: '70%' }} />
                    {searchTypes.map((type) => <MenuItem key={type.value}
                      onClick={(event) => handleSearchTypeClick(event, type)}>
                      <Checkbox checked={selectedSearchTypes.map(s => s.value)?.includes(type?.value)} />
                      <ListItemText primary={type.label} />
                    </MenuItem>)}
                  </Select>
                </Grid>
                <Grid item xs="auto">
                  {searchChildren}
                </Grid>
                <Grid item xs="auto">
                  <DateFilter
                    filters={filters}
                    setFilters={setFilters}
                    sx={{
                      '& input': {width: !filters?.startDate && !filters?.endDate ? '100px' : 'auto' },
                      '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #dee4e9' },
                      '& fieldset': {borderColor: '#dee4e9 !important',
                      },
                      '&:hover fieldset': { borderColor: '#dee4e9 !important' },
                      '&.Mui-focused fieldset': { borderColor: '#dee4e9 !important' },
                    }}
                  />
                </Grid>
                <Grid item xs sx={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    value={searchValue}
                    onChange={(e) => setSearchData(data => ({ ...data, page: 1, searchValue: e.target.value }))}
                    variant="outlined"
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!isSearchDisabled) {
                          handleSearch(searchData, 1);
                        }
                      }
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      width: '100%',
                      height: '40px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f2f5f7',
                        '& fieldset': { borderColor: '#dee4e9' },
                        '&:hover fieldset': { borderColor: '#dee4e9' },
                        '&.Mui-focused fieldset': { borderColor: '#dee4e9' }
                      },
                    }}
                    size="small"
                    placeholder="Search..."
                    InputProps={{
                      startAdornment: (
                        <Tooltip title="Advanced search" arrow>
                          <IconButton color="secondary">
                            <Iconify icon={'fa6-brands:searchengin'} />
                          </IconButton>
                        </Tooltip>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setSearchData(data => ({ ...data, page: 1, searchValue: '' }))}>
                            <Iconify icon={'ri:close-fill'} sx={{ cursor: 'pointer' }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs="auto">
                  <Button
                    variant="contained"
                    disabled={isSearchDisabled}
                    sx={{ boxShadow: 'none', height: '40px' }}
                    onClick={() => handleSearch(searchData, 1)}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{my: 1}}>
              {filtersElements}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {searchResults?.searchResults?.length > 0 && !loading ?
      <Scrollbar sx={{flexGrow: 1}}>
        <Grid container direction="row">
          <Grid item xs={1}/>
          <Grid item xs={11} sx={{height: '100%'}}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
              {searchDataMap}
            </Box>
          </Grid>
          </Grid>
        </Scrollbar> :
        loading ? <Box
          sx={{
            zIndex: 999999999999999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            textAlign: 'center',
            height: '100%',
            alignContent: 'center'
          }}
        >
          <LoadingAnimation />
        </Box> : 
        <Box sx={{textAlign: 'center', mt: 3, alignContent: 'center'}}>
          <Typography variant="subtitle1" color="text.secondary">
            No search results found
          </Typography>
        </Box>
      }
      {searchResults?.searchResults?.length > 0 && searchResults?.totalCount > 20 && !loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Pagination
            count={Math.ceil(searchResults?.totalCount / (searchData.pageSize || 20), 10)}
            shape="rounded"
            onChange={(_, val) => handlePaginationChange(val)}
            page={page ?? 1}
          />
          <Select
            value={searchData.pageSize || 20}
            onChange={e => setSearchData(data => ({ ...data, pageSize: e.target.value, page: 1 }))}
            size="small"
            sx={{ minWidth: 80, mx: 1, p: 0, '& .MuiSelect-select': { py: 0.5, px: 1 } }}
          >
            {[10, 20, 50, 100, 150].map(size => (
              <MenuItem key={size} value={size} sx={{ py: 0.5, px: 1 }}>{size} / page</MenuItem>
            ))}
          </Select>
        </Box>
      ) : null}
    </Box>
  );
  }
